/**
 * saleAllocationMassUploadApi — RTK Query endpoints for the Sales Control ›
 * Sales Allocation "Mass Upload" dialog (Access `frmCtrlSalesQrySaleAlcAddMany`,
 * opened from `btnInsert`/"Mass Upload"). Mass-insert MANY rows into
 * `public_sales_allocation_scs` in one shot.
 *
 * Injected into the shared `apiSlice` so it reuses the same base query + the
 * existing `'SaleControl'` tag — bulk insert invalidates the Sales Allocation
 * list (it providesTags `'SaleControl'`), mirroring Access
 * `Forms!frmCtrlSalesMain!navCtrlSales.Form!subForm.Requery`.
 *
 * ⚠️ BACKEND NOT BUILT YET. Every endpoint here is a FE contract + TODO; until
 * the routes exist they 404 and the dialog degrades gracefully:
 *   - combo `useSaleAlcMassClientsQuery` → `data?.items ?? []` (empty combo)
 *   - lookups → `data?.items ?? []`
 *   - validate / bulk-create surface the error via snackbar (no silent success).
 * No fake data is fabricated anywhere.
 */
import { apiSlice } from './apiSlice'

export interface SaleAlcMassLookupItem {
  value: string
  label: string
}
export interface SaleAlcMassLookupResponse {
  items: SaleAlcMassLookupItem[]
}

/**
 * One row of the mass-insert payload. Keys map 1:1 to the INSERT column list in
 * Access `btnSave_Click` (temp table `public_tmp_addmany_salesalc` → target
 * `public_sales_allocation_scs`). Strings empty/optional → BE coerces to NULL.
 */
export interface SaleAlcMassRow {
  sale_contract: string
  delivery_order: string
  m_date: string | null
  m_truck_no: string
  from_warehouse: string
  to_warehouse: string
  m_net_weight: number | null
  container_no: string
  seal_no: string
  number_bags: number | null
  tare_weight: number | null
  tare_carton_pallet: number | null
  tare_cont: number | null
  max_gross_weight: number | null
  allocating_bag: number | null
  allocating_quantity: number | null
}

/** Per-row validation error returned by the dry-run / bulk endpoints. */
export interface SaleAlcMassRowError {
  field?: keyof SaleAlcMassRow
  message: string
}
export interface SaleAlcMassRowVerdict {
  ok: boolean
  errors: SaleAlcMassRowError[]
}
/** Dry-run cross-check result (one verdict per submitted row, same order). */
export interface SaleAlcMassValidateResponse {
  rows: SaleAlcMassRowVerdict[]
}
/** Bulk insert result. */
export interface SaleAlcMassCreateResponse {
  created: number
}

interface SaleAlcMassRequest {
  /** Optional Client filter (Access `cp_vn` combo) — scopes contract lookup. */
  client?: string
  rows: SaleAlcMassRow[]
}

const saleAllocationMassUploadApi = apiSlice.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // ── Header combo (Access `cp_vn`, RowSource:
    //    SELECT code, name FROM public_cp_vn WHERE is_status = 0 ORDER BY code) ──
    // TODO BE: GET /sale-controls/sale-alc/mass/clients → { items: [{ value, label }] }
    saleAlcMassClients: builder.query<SaleAlcMassLookupResponse, string | void>({
      query: (q) => ({
        url: '/sale-controls/sale-alc/mass/clients',
        params: q ? { q } : {},
      }),
    }),

    // ── Grid combo lookups (Access btnSave existence checks) ────────────────
    // Contract No must exist in public_mv_local_s (contract_sale); the Client
    // (`cp_vn`) optionally scopes the list.
    // TODO BE: GET /sale-controls/sale-alc/mass/contracts → { items: [{ value, label }] }
    saleAlcMassContracts: builder.query<
      SaleAlcMassLookupResponse,
      { q?: string; client?: string } | void
    >({
      query: (arg) => ({
        url: '/sale-controls/sale-alc/mass/contracts',
        params: {
          ...(arg && arg.q ? { q: arg.q } : {}),
          ...(arg && arg.client ? { client: arg.client } : {}),
        },
      }),
    }),
    // DO No must exist in public_v_delivery_order_scs (do_no, is_status=0).
    // TODO BE: GET /sale-controls/sale-alc/mass/delivery-orders → { items: [{ value, label }] }
    saleAlcMassDeliveryOrders: builder.query<SaleAlcMassLookupResponse, string | void>({
      query: (q) => ({
        url: '/sale-controls/sale-alc/mass/delivery-orders',
        params: q ? { q } : {},
      }),
    }),
    // from_warehouse / to_warehouse picklist (shared with single Add form).
    // TODO BE: GET /sale-controls/sale-alc/mass/warehouses → { items: [{ value, label }] }
    saleAlcMassWarehouses: builder.query<SaleAlcMassLookupResponse, string | void>({
      query: (q) => ({
        url: '/sale-controls/sale-alc/mass/warehouses',
        params: q ? { q } : {},
      }),
    }),

    // ── Dry-run cross-check (Access btnSave per-row validation loop) ─────────
    // Mirrors the VBA error scan: Contract No exists, DO No exists, Seal No not
    // already used in public_mv_sales_allocation_scs. Returns one verdict per row.
    // TODO BE: POST /sale-controls/sale-alc/mass/validate { client, rows } → { rows: [...] }
    validateSaleAlcMass: builder.mutation<SaleAlcMassValidateResponse, SaleAlcMassRequest>({
      query: (body) => ({
        url: '/sale-controls/sale-alc/mass/validate',
        method: 'POST',
        body,
      }),
    }),

    // ── Bulk insert (Access btnSave INSERT loop, one transaction) ───────────
    // BE re-validates (race-safe) then inserts all rows; on success the parent
    // list refetches via the shared 'SaleControl' tag (= Access subForm.Requery).
    // TODO BE: POST /sale-controls/sale-alc/mass { client, rows } → { created }
    bulkCreateSaleAlcMass: builder.mutation<SaleAlcMassCreateResponse, SaleAlcMassRequest>({
      query: (body) => ({
        url: '/sale-controls/sale-alc/mass',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useSaleAlcMassClientsQuery,
  useSaleAlcMassContractsQuery,
  useSaleAlcMassDeliveryOrdersQuery,
  useSaleAlcMassWarehousesQuery,
  useValidateSaleAlcMassMutation,
  useBulkCreateSaleAlcMassMutation,
} = saleAllocationMassUploadApi

export default saleAllocationMassUploadApi
